import { useMemo } from 'react';
import { StepsType } from 'core/stepper/Stepper.types';
import { useGetSteps } from 'components/Stepper/useGetSteps';
import ProposalProductSelection from 'widgets/ProposalManagement/CreateProposal/ProposalProductSelection';
import ProductSelectionThreeB from 'widgets/ProposalManagement/CreateProposal/ProductSelectionThreeB/ProductSelectionThreeB';
import ProposalAgentInformation from 'widgets/ProposalManagement/CreateProposal/ProposalAgentInformation';
import FinalizeProposal from 'widgets/ProposalManagement/CreateProposal/FinalizeProposal';
import useProposalProductSelection3b from 'datasource/ProposalManagement/CreateProposalManager/hooks/useProposalProductSelection3b';
import { useProposalProductSelection } from 'datasource/ProposalManagement/CreateProposalManager/hooks/useProposalProductSelection';
import { useProposalAgentInformation } from 'datasource/ProposalManagement/CreateProposalManager/hooks/useProposalAgentInformation';
import { useFinalizeProposal } from 'datasource/ProposalManagement/CreateProposalManager/hooks/useFinalizeProposal';
import { useCreateProposal } from 'datasource/ProposalManagement/CreateProposalManager/CreateProposalManager';

export const stepsKeys = {
    ProposalProductSelection: 'proposal-product-selection',
    ProposalProductSelectionFor3b: 'proposal-product-selection-for-3b',
    ProposalAgentInformation: 'proposal-agent-information',
    FinalizeProposal: 'finalize-proposal',
    Summary: 'summary',
};

export const process = {
    main: 'parent',
};

const useCreateNewProposalFlow = () => {
    const { is3bProductGroupSelector } = useCreateProposal();

    const createNewProposalFlow: StepsType = useMemo(() => ({
        [stepsKeys.ProposalProductSelection]: {
            datasource: useProposalProductSelection,
            process: process.main,
            stepperTitle: 'createNewProposal.ProposalProductSelection.Content.title',
            contentTitle: 'createNewProposal.ProposalProductSelection.Content.title',
            contentClassName: 'create-new-proposal proposal-product-selection',
            component: ProposalProductSelection,
            url: 'proposal-product-selection',
            next: [
                {
                    target: stepsKeys.ProposalAgentInformation,
                    condition: () => !is3bProductGroupSelector,
                },
                {
                    target: stepsKeys.ProposalProductSelectionFor3b,
                    condition: () => !!is3bProductGroupSelector,
                },
            ],
            isBegin: true,
        },
        [stepsKeys.ProposalProductSelectionFor3b]: {
            datasource: useProposalProductSelection3b,
            process: process.main,
            stepperTitle: 'createNewProposal.ProposalProductSelectionFor3b.Content.title',
            contentTitle: 'createNewProposal.ProposalProductSelectionFor3b.Content.title',
            contentClassName: 'create-new-proposal proposal-product-selection-for-3b',
            component: ProductSelectionThreeB,
            url: 'proposal-product-selection-for-3b',
            prev: [
                {
                    target: stepsKeys.ProposalProductSelection,
                    condition: () => true,
                },
            ],
            next: [
                {
                    target: stepsKeys.ProposalAgentInformation,
                    condition: () => true,
                },
            ],
        },
        [stepsKeys.ProposalAgentInformation]: {
            datasource: useProposalAgentInformation,
            process: process.main,
            stepperTitle: 'portfolio.createNewPortfolio.agentInformation',
            contentTitle: 'portfolio.createNewPortfolio.agentInformation',
            contentClassName: 'create-new-proposal proposal-agent-information',
            component: ProposalAgentInformation,
            url: 'proposal-agent-information',
            prev: [
                {
                    target: stepsKeys.ProposalProductSelection,
                    condition: () => true,
                },
            ],
            next: [
                {
                    target: stepsKeys.FinalizeProposal,
                    condition: () => true,
                },
            ],
        },
        [stepsKeys.FinalizeProposal]: {
            datasource: useFinalizeProposal,
            process: process.main,
            stepperTitle: 'portfolio.createNewPortfolio.Submit',
            contentTitle: 'createNewProposal.FinalizeProposal.Content.title',
            contentClassName: 'create-new-proposal submit-proposal',
            component: FinalizeProposal,
            url: 'finalize-proposal',
            prev: [
                {
                    target: stepsKeys.ProposalAgentInformation,
                    condition: () => true,
                },
            ],
            isEnd: true,
        },
    }), [is3bProductGroupSelector]);

    const { stepsBasedOnState } = useGetSteps({ flow: createNewProposalFlow, initialStep: stepsKeys.ProposalProductSelection });

    return { stepsBasedOnState };
};

export default useCreateNewProposalFlow;
